'use client';
import { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, Clock, Copy, MessageCircle, Users } from 'lucide-react';

/**
 * /partner-dashboard?t={token} — 夥伴個人儀表板（R3）
 * 會話 24h（WhatsApp 登入取得）；KPI 卡＋專屬連結＋成交明細。
 */

const CF_WORKER_URL = 'https://jkd-whatsapp-verification.jimsbond007.workers.dev';

interface PartnerData {
  name: string; code: string; status: string;
  clicks: number; leads: number; verified: number;
  wonAmount12m: number; commPending: number; commApproved: number; commPaid: number;
}
interface DealRow {
  id: string; amount: number; rate: number; commission: number; status: string;
  note?: string; createdAt?: number;
}

export default function PartnerDashboardPage() {
  const [data, setData] = useState<{ partner: PartnerData; deals: DealRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('t');
    if (!t) { setError('missing_session'); setLoading(false); return; }
    fetch(`${CF_WORKER_URL}/partner/dashboard?t=${encodeURIComponent(t)}`)
      .then(async (res) => {
        const d = await res.json();
        if (!res.ok || !d.success) throw new Error(d.error || `HTTP ${res.status}`);
        return d;
      })
      .then((d) => setData({ partner: d.partner, deals: d.deals || [] }))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const referralUrl = data ? `https://jkdcoding.com/gf/${data.partner.code}` : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `一位朋友推薦我試試 JKD Coding 的免費 AI 轉化診斷（30 分鐘，無銷售壓力）：${referralUrl}`,
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-jkd-black text-white flex items-center justify-center">
        <p className="text-gray-500">載入中…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-jkd-black text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center card-dark">
          <p className="text-gray-400 mb-6">
            連結無效或已過期（會話 24 小時）。
            <br />
            <span className="text-gray-500">Session invalid or expired — please log in again.</span>
          </p>
          <a href="https://jkdcoding.com/partner" className="btn-gold inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> 回夥伴頁重新登入 / Back
          </a>
        </div>
      </div>
    );
  }

  const p = data.partner;
  const totalComm = (p.commPending || 0) + (p.commApproved || 0) + (p.commPaid || 0);

  return (
    <div className="min-h-screen bg-jkd-black text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => (window.location.href = '/')} className="inline-flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors mb-8">
          <ArrowLeft className="w-5 h-5" /> 返回主頁
        </button>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <h1 className="text-3xl font-black text-white">夥伴儀表板</h1>
            <p className="text-gray-400 mt-1">
              {p.name} · <span className="font-mono text-jkd-gold">/gf/{p.code}</span> ·{' '}
              <span className={p.status === 'active' ? 'text-green-400' : 'text-yellow-400'}>
                {p.status === 'active' ? '已審核（佣金生效）' : p.status === 'pending' ? '待審核（分享與歸因已生效，佣金待通過）' : `狀態：${p.status}`}
              </span>
            </p>
          </div>
          <span className="text-xs text-gray-500 font-mono">會話 24 小時有效</span>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          {[
            { label: '連結點擊', value: String(p.clicks || 0), cls: 'text-white' },
            { label: '歸因 Lead', value: String(p.leads || 0), cls: 'text-white' },
            { label: '已驗證', value: String(p.verified || 0), cls: 'text-white' },
            { label: '成交額 12m', value: `HK$${(p.wonAmount12m || 0).toLocaleString()}`, cls: 'text-white' },
            { label: '佣金待批', value: `HK$${(p.commPending || 0).toLocaleString()}`, cls: 'text-yellow-400' },
            { label: '已付佣金', value: `HK$${(p.commPaid || 0).toLocaleString()}`, cls: 'text-green-400' },
          ].map((k) => (
            <div key={k.label} className="card-dark">
              <p className="text-[11px] font-mono uppercase tracking-wider text-gray-500">{k.label}</p>
              <p className={`text-2xl font-black mt-2 ${k.cls}`}>{k.value}</p>
            </div>
          ))}
        </div>

        {/* 專屬連結 */}
        <div className="card-dark card-gold-border p-6 mb-8">
          <h2 className="text-lg font-bold text-white mb-1">你的專屬推薦連結</h2>
          <p className="text-gray-500 text-xs mb-4">分享給有 AI / 系統需要的客戶圈子——他們完成驗證即記錄在你名下（終生綁定）。</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-jkd-black border border-jkd-gray-200 rounded px-4 py-3 font-mono text-sm text-jkd-gold truncate" dir="ltr">
              {referralUrl}
            </div>
            <button onClick={copyLink} className="btn-outline text-sm px-5 py-3 inline-flex items-center justify-center gap-2 whitespace-nowrap">
              {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {copied ? '已複製！' : '複製推薦文案'}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`一位朋友推薦我試試 JKD Coding 的免費 AI 轉化診斷（30 分鐘，無銷售壓力）：${referralUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold text-sm px-5 py-3 inline-flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp 分享
            </a>
          </div>
        </div>

        {/* 佣金總覽 */}
        <div className="card-dark p-6 mb-8">
          <h2 className="text-lg font-bold text-white mb-4">佣金狀態（月結）</h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-black text-yellow-400">HK${(p.commPending || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">待審核（成交後計）</p>
            </div>
            <div>
              <p className="text-2xl font-black text-blue-400">HK${(p.commApproved || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">已審核待付</p>
            </div>
            <div>
              <p className="text-2xl font-black text-green-400">HK${(p.commPaid || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">已付佣金</p>
            </div>
          </div>
          {totalComm === 0 && (
            <p className="text-center text-xs text-gray-600 mt-4">佣金於推薦客戶成交收款後產生——繼續分享你的連結！</p>
          )}
        </div>

        {/* 成交明細 */}
        <div className="card-dark overflow-x-auto">
          <h2 className="text-lg font-bold text-white mb-4">成交明細</h2>
          {data.deals.length === 0 ? (
            <p className="text-sm text-gray-500">暫無成交紀錄。</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs font-mono uppercase tracking-wider border-b border-jkd-gray-200">
                  <th className="py-2 pr-4">日期</th>
                  <th className="py-2 pr-4">實收</th>
                  <th className="py-2 pr-4">佣金率</th>
                  <th className="py-2 pr-4">佣金</th>
                  <th className="py-2">狀態</th>
                </tr>
              </thead>
              <tbody>
                {data.deals.map((d) => (
                  <tr key={d.id} className="border-b border-jkd-gray-200/50 last:border-0">
                    <td className="py-2 pr-4 text-gray-300 font-mono">{d.createdAt ? new Date(d.createdAt).toLocaleDateString('zh-HK') : '—'}</td>
                    <td className="py-2 pr-4 text-white">HK${d.amount.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-gray-400">{d.rate}%</td>
                    <td className="py-2 pr-4 text-jkd-gold font-bold">HK${d.commission.toLocaleString()}</td>
                    <td className="py-2">
                      {d.status === 'paid' ? (
                        <span className="inline-flex items-center gap-1 text-green-400"><CheckCircle className="w-3.5 h-3.5" /> 已付</span>
                      ) : d.status === 'approved' ? (
                        <span className="inline-flex items-center gap-1 text-blue-400"><CheckCircle className="w-3.5 h-3.5" /> 待付</span>
                      ) : d.status === 'cancelled' ? (
                        <span className="text-red-400">已取消</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-yellow-400"><Clock className="w-3.5 h-3.5" /> 待審核</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-8 text-center">
          <Users className="w-6 h-6 text-jkd-gold/40 mx-auto mb-2" />
          <p className="text-xs text-gray-600">JKD Partner Program · 佣金以實收金額計算 · 每月 1 號結算 · 如有疑問 WhatsApp 查詢</p>
        </div>
      </div>
    </div>
  );
}
